/**
 * The System Settings modal's section catalog and its address vocabulary
 * (FRONT-2376).
 *
 * Shape copied from `lib/settings-sections.ts`, which does exactly this for
 * the User Settings modal (`/settings`) — a groups array, a sections array
 * carrying each entry's group, an address vocabulary, and a pure resolver the
 * live route and its legacy redirects both read. Copied rather than shared:
 * the two catalogs are unrelated (different sections, different gating), and
 * sharing the type would tie one modal's address rules to the other's.
 *
 * ## Why this modal exists
 *
 * FRONT-2376 emptied the left sidebar of eight destinations that were really
 * settings, not daily workspace navigation — Policy, Tools, Connections,
 * Audit log, Org Settings, Sandbox, Status and Diagnostics. They did not move
 * to User Settings (`/settings`): that modal is about YOU (identity, models,
 * knowledge, your own machine), these eight are about the SYSTEM (policy the
 * whole tenant runs under, tools everyone can call, the org's own settings) —
 * hence a second modal, sharing the first one's chrome
 * (`features/settings/settings-modal-shell.tsx`) but its own catalog, its own
 * `?section=` vocabulary, and its own landing section.
 */

import {
  ScalesIcon,
  WrenchIcon,
  PlugIcon,
  ClipboardTextIcon,
  BuildingsIcon,
  CubeIcon,
  AppWindowIcon,
  PulseIcon,
  StethoscopeIcon,
  PaletteIcon,
  GlobeIcon,
  type Icon,
} from '@phosphor-icons/react';
import type { SectionAvailability } from '~/lib/settings-sections';
import { BRAND_KIT_FLAG } from '~/lib/feature-flag-names';

export type { SectionAvailability };

export type SystemSettingsGroupId = 'general' | 'admin' | 'health';

export const SYSTEM_SETTINGS_GROUPS: { id: SystemSettingsGroupId; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'admin', label: 'Admin' },
  { id: 'health', label: 'Health' },
];

export interface SystemSettingsSectionEntry {
  id: SystemSettingsSectionId;
  label: string;
  group: SystemSettingsGroupId;
  /** Reuses the exact icon the sidebar catalog used for this destination
   *  before FRONT-2376 removed it from there — same visual identity, on the
   *  catalog itself (unlike `SettingsSectionEntry`, which keeps icons out of
   *  its own catalog) because BOTH the rail and the command palette need
   *  one here, and a brand new catalog has no text-only history to honor. */
  icon: Icon;
  /**
   * True when the panel holds an unsaved draft in component state, which is
   * what earns it the force-mount treatment in `routes/system-settings.tsx` —
   * same reasoning as `SettingsSectionEntry.holdsDraft`. Org Settings and
   * Sandbox both carry their own `ActionBar` + local draft state that resets
   * the instant the panel remounts, so both are marked here; the other six
   * hold nothing worth losing across a rail switch.
   */
  holdsDraft?: boolean;
  /**
   * `'admin'` for the sections that are admin-only: Org Settings, Apps and
   * Sandbox, plus Diagnostics (FRONT-2432). Diagnostics' org-wide reads
   * (spend, activity, graph health) are admin-gated on the backend; its
   * self-scoped probes (your own session, one agent's wiring) stay open
   * there but are no longer reachable from the UI for a non-admin.
   * Absent for every other section.
   */
  gate?: 'admin';
  /**
   * Feature flag that must be on for the section to exist at all (FRONT-1822),
   * on top of `gate`. Same "off means gone" posture as `SettingsSectionEntry.flag`.
   */
  flag?: string;
}

export const SYSTEM_SETTINGS_SECTION_IDS = [
  'policy',
  'tools',
  'connections',
  'domains',
  'audit',
  'org-settings',
  'app-policy',
  'brand',
  'sandbox',
  'diagnostics',
  'status',
] as const;

export type SystemSettingsSectionId = (typeof SYSTEM_SETTINGS_SECTION_IDS)[number];

export const SYSTEM_SETTINGS_SECTIONS: SystemSettingsSectionEntry[] = [
  // General
  { id: 'policy', label: 'Policy', group: 'general', icon: ScalesIcon },
  { id: 'tools', label: 'Tools', group: 'general', icon: WrenchIcon },
  { id: 'connections', label: 'Connections', group: 'general', icon: PlugIcon },
  // FRONT-3179: the workspace's verified domains. Every member sees it (a colleague asked to add a DNS record lands
  // here); only an admin can remove a domain, which the server enforces.
  { id: 'domains', label: 'Domains', group: 'general', icon: GlobeIcon },
  // Admin
  { id: 'audit', label: 'Audit Log', group: 'admin', icon: ClipboardTextIcon },
  {
    id: 'org-settings',
    label: 'Org Settings',
    group: 'admin',
    icon: BuildingsIcon,
    gate: 'admin',
    holdsDraft: true,
  },
  // FRONT-2410: what apps built in this workspace may use.
  {
    id: 'app-policy',
    label: 'Apps',
    group: 'admin',
    icon: AppWindowIcon,
    gate: 'admin',
    holdsDraft: true,
  },
  // FRONT-1822: the workspace brand kit — logos, colours, fonts, style guide.
  {
    id: 'brand',
    label: 'Brand',
    group: 'admin',
    icon: PaletteIcon,
    gate: 'admin',
    holdsDraft: true,
    flag: BRAND_KIT_FLAG,
  },
  {
    id: 'sandbox',
    label: 'Sandbox',
    group: 'admin',
    icon: CubeIcon,
    gate: 'admin',
    holdsDraft: true,
  },
  // Admin-only since FRONT-2432, so it sits with the other admin sections
  // rather than under Health, which every signed-in user sees (FRONT-1612).
  { id: 'diagnostics', label: 'Diagnostics', group: 'admin', icon: StethoscopeIcon, gate: 'admin' },
  // Health
  { id: 'status', label: 'Status', group: 'health', icon: PulseIcon },
];

/** The section a bare `/system-settings` opens on. */
export const DEFAULT_SYSTEM_SETTINGS_SECTION: SystemSettingsSectionId = 'policy';

export function isSystemSettingsSection(
  value: string | null | undefined,
): value is SystemSettingsSectionId {
  return !!value && (SYSTEM_SETTINGS_SECTION_IDS as readonly string[]).includes(value);
}

const SECTION_BY_ID: ReadonlyMap<string, SystemSettingsSectionEntry> = new Map(
  SYSTEM_SETTINGS_SECTIONS.map((s) => [s.id, s]),
);

export function getSystemSettingsSection(id: SystemSettingsSectionId): SystemSettingsSectionEntry {
  const found = SECTION_BY_ID.get(id);
  // Unreachable while `id` is typed — a real throw rather than a fallback so
  // a section id added to the union without an entry here fails loudly at
  // the first render instead of rendering an empty rail row.
  if (!found) throw new Error(`unknown system settings section: ${id}`);
  return found;
}

/** The sections whose panels stay mounted once visited. */
export const DRAFT_HOLDING_SYSTEM_SECTIONS: ReadonlySet<SystemSettingsSectionId> = new Set(
  SYSTEM_SETTINGS_SECTIONS.filter((s) => s.holdsDraft).map((s) => s.id),
);

/**
 * `/system-settings` plus the section, with the landing section left
 * implicit so the canonical URL of the page you arrive on has no query
 * string at all.
 */
export function systemSettingsPath(section: SystemSettingsSectionId): string {
  return section === DEFAULT_SYSTEM_SETTINGS_SECTION
    ? '/system-settings'
    : `/system-settings?section=${section}`;
}

/**
 * `systemSettingsPath(section)` plus whatever OTHER params `rememberedQuery`
 * carries (Org Settings' own `?tab=announcements`, say) — FRONT-2376 review
 * item 5: a plain rail switch writes the bare `systemSettingsPath`, which
 * drops any such param a section's own panel manages, so switching away and
 * back landed on that panel's default sub-tab instead of where it was left.
 * `use-system-settings-section.ts` remembers each section's last extra query
 * string (in memory, per modal instance) and passes it back in here when
 * navigating TO that section. `rememberedQuery` is never expected to carry
 * `section` itself, but it is stripped defensively so a stale one can never
 * fight the `section` argument this function was actually called with.
 */
export function systemSettingsPathWithQuery(
  section: SystemSettingsSectionId,
  rememberedQuery: string | undefined,
): string {
  const base = systemSettingsPath(section);
  if (!rememberedQuery) return base;
  const extra = new URLSearchParams(rememberedQuery);
  extra.delete('section');
  const qs = extra.toString();
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
}

/**
 * The eight bare legacy paths that used to be full pages, and the section
 * each lands on now. Exported so the redirect and its own test can share one
 * table rather than two chances to disagree about a mapping.
 */
export const LEGACY_SYSTEM_SETTINGS_PATHS: Record<string, SystemSettingsSectionId> = {
  '/policy': 'policy',
  '/tools': 'tools',
  '/connections': 'connections',
  '/audit': 'audit',
  '/org-settings': 'org-settings',
  '/admin/sandbox': 'sandbox',
  '/status': 'status',
  '/diagnostics': 'diagnostics',
};

const stripQuestion = (value: string | undefined): string => (value ?? '').replace(/^\?/, '');
const stripHash = (value: string | undefined): string => (value ?? '').replace(/^#/, '');

/**
 * Translate a visit to one of the eight legacy bare paths into its
 * `/system-settings` equivalent, carrying over every OTHER query param (e.g.
 * `/org-settings?tab=x` → `/system-settings?section=org-settings&tab=x`) and
 * the hash untouched. `section` is always written FIRST — cosmetic only, but
 * it is what keeps the rewritten address readable rather than an
 * alphabetically-shuffled query string.
 *
 * `pathname` not being one of the eight keys falls back to the landing
 * section rather than throwing — defensive only; every route registration
 * that points here uses one of the exact keys.
 */
export function legacySystemSettingsTarget(
  pathname: string,
  search: string | undefined,
  hash: string | undefined,
): string {
  const section = LEGACY_SYSTEM_SETTINGS_PATHS[pathname] ?? DEFAULT_SYSTEM_SETTINGS_SECTION;
  const incoming = new URLSearchParams(stripQuestion(search));
  incoming.delete('section');
  const params = new URLSearchParams();
  params.set('section', section);
  for (const [key, value] of incoming) params.append(key, value);
  const qs = params.toString();
  const cleanHash = stripHash(hash);
  return `/system-settings${qs ? `?${qs}` : ''}${cleanHash ? `#${cleanHash}` : ''}`;
}

/**
 * What the live `/system-settings` page should show for a given address, and
 * whether that address needs rewriting first — the counterpart to
 * `resolveSettingsLocation`, simplified: this vocabulary is new, so there is
 * no legacy hash table to carry over the way `/settings` has to.
 *
 * `opts.availability` (admin gating) answers, for each of the two admin-only
 * sections, whether it is `'available'`, `'pending'` (the boot query hasn't
 * answered yet) or `'hidden'` (not an admin). A requested section that is
 * `'hidden'` is treated exactly like one `isSystemSettingsSection` would
 * reject (falls through to the default, redirect included — never renders
 * the admin panel to a non-admin); `'pending'` short-circuits to "render what
 * was asked for, redirect nothing" — bouncing a direct link to an admin
 * section to the default the instant before the admin check comes back would
 * be worse than a brief wait.
 */
export interface ResolveSystemSettingsLocationOptions {
  availability?: (id: SystemSettingsSectionId) => SectionAvailability;
}

/** Retired section ids and where they live now, so a bookmarked link keeps working. */
export const SYSTEM_SETTINGS_SECTION_ALIASES: Record<string, SystemSettingsSectionId> = {
  'website-connections': 'connections',
};

export function resolveSystemSettingsLocation(
  search: string | undefined,
  opts?: ResolveSystemSettingsLocationOptions,
): { section: SystemSettingsSectionId; redirectTo: string | null } {
  const raw = new URLSearchParams(stripQuestion(search)).get('section');
  // FRONT-2558: websites moved into Connections; an old link still lands there.
  const alias = raw ? SYSTEM_SETTINGS_SECTION_ALIASES[raw] : undefined;
  if (alias) return { section: alias, redirectTo: systemSettingsPath(alias) };
  const availabilityOf = (id: SystemSettingsSectionId): SectionAvailability =>
    opts?.availability ? opts.availability(id) : 'available';

  if (isSystemSettingsSection(raw)) {
    const availability = availabilityOf(raw);
    if (availability === 'pending') return { section: raw, redirectTo: null };
    if (availability === 'available') return { section: raw, redirectTo: null };
    // 'hidden' — fall through to the default, scrubbing the address rather
    // than leaving it claiming a section this modal will not render.
  }

  const stray = raw !== null;
  return {
    section: DEFAULT_SYSTEM_SETTINGS_SECTION,
    redirectTo: stray ? systemSettingsPath(DEFAULT_SYSTEM_SETTINGS_SECTION) : null,
  };
}

/**
 * Query params that open a one-shot flow on the page that reads them
 * (`?new=1` via `use-new-query-flag.ts`, read by the embedded Tools and
 * Connections panels among others) and strip themselves from the address
 * the instant they fire. Never worth remembering as a section's "own extra
 * state" — see `rememberedExtraQuery` below.
 */
export const SYSTEM_SETTINGS_ONE_SHOT_QUERY_PARAMS = ['new'] as const;

/**
 * What `use-system-settings-section.ts` should remember as SECTION's own
 * extra query string, given the address currently in the bar — a pure
 * decision (same reasoning as `resolveBackTarget` in `use-modal-close.ts`)
 * so the three failure modes FRONT-2376 review round 2 (item 5) found are
 * each a named, unit-testable case rather than inline render logic no test
 * exercised:
 *
 * 1. Returns `null` — meaning "record nothing" — whenever this render is a
 *    FALLBACK for an invalid or not-yet-visible `?section=`
 *    (`isFallbackForInvalidSection`, i.e. `resolveSystemSettingsLocation`
 *    returned a non-null `redirectTo`). The extras on that stray address
 *    were never meant for the fallback section, so they must not pollute
 *    its memory while the corrective `replace` is still in flight.
 * 2. Otherwise always returns a string — including `""` — so a caller that
 *    writes it unconditionally CLEARS a previously-remembered value the
 *    moment the section is visited with no extra params, rather than only
 *    ever recording a truthy one and leaving a stale value to reappear
 *    later.
 * 3. Strips `SYSTEM_SETTINGS_ONE_SHOT_QUERY_PARAMS` before returning, so a
 *    one-shot flag caught mid-flight (the render before the panel's own
 *    effect strips it from the URL) is never remembered and replayed on a
 *    later visit.
 */
export function rememberedExtraQuery(
  search: string | undefined,
  opts: { isFallbackForInvalidSection: boolean },
): string | null {
  if (opts.isFallbackForInvalidSection) return null;
  const params = new URLSearchParams(stripQuestion(search));
  params.delete('section');
  for (const oneShot of SYSTEM_SETTINGS_ONE_SHOT_QUERY_PARAMS) params.delete(oneShot);
  return params.toString();
}
